import { useEffect, useRef, useState } from 'react';

/** Once a given loading label is on screen, keep that copy at least this long. */
export const LOADING_LABEL_MIN_VISIBLE_MS = 200;

/**
 * Shows while `pending` is true. Once shown, stays true for at least `minVisibleMs`
 * after `pending` clears. When `holdKey` changes while visible, the min-visible
 * clock restarts for the new key.
 */
export function useMinVisible(
  pending: boolean,
  minVisibleMs = LOADING_LABEL_MIN_VISIBLE_MS,
  holdKey?: string
): boolean {
  // Start visible when work is already pending so "Loading Badges" isn't blank for a frame.
  const [visible, setVisible] = useState(pending);
  const visibleSinceRef = useRef<number | null>(pending ? performance.now() : null);
  const holdKeyRef = useRef(holdKey);
  const hideTimerRef = useRef<number | undefined>();

  useEffect(() => {
    const clearHide = () => {
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = undefined;
      }
    };

    const phaseChanged = holdKey !== holdKeyRef.current;
    holdKeyRef.current = holdKey;

    if (phaseChanged) {
      clearHide();
      visibleSinceRef.current = performance.now();
      setVisible(true);
      if (pending) return clearHide;
      hideTimerRef.current = window.setTimeout(() => {
        hideTimerRef.current = undefined;
        visibleSinceRef.current = null;
        setVisible(false);
      }, minVisibleMs);
      return clearHide;
    }

    if (pending) {
      clearHide();
      if (visibleSinceRef.current == null) {
        visibleSinceRef.current = performance.now();
        setVisible(true);
      }
      return clearHide;
    }

    if (visibleSinceRef.current == null) {
      setVisible(false);
      return clearHide;
    }

    const remaining = Math.max(0, minVisibleMs - (performance.now() - visibleSinceRef.current));
    clearHide();
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = undefined;
      visibleSinceRef.current = null;
      setVisible(false);
    }, remaining);
    return clearHide;
  }, [pending, minVisibleMs, holdKey]);

  return visible;
}

/**
 * Sticky loading copy: each phase (Badges → Profiles) stays on screen for at least
 * `minVisibleMs` before the text advances. No blank gap — Badges remains until Profiles takes over.
 * Pass a rising `snapEpoch` to jump straight to `targetPhase` (e.g. replaying boot after submit).
 */
export function useLoadingLabel(
  targetPhase: string,
  workPending: boolean,
  minVisibleMs = LOADING_LABEL_MIN_VISIBLE_MS,
  snapEpoch?: number
): { visible: boolean; label: string } {
  const [label, setLabel] = useState(targetPhase);
  const labelShownAtRef = useRef<number | null>(null);
  const snapEpochRef = useRef(snapEpoch);

  // Keep chrome up while work remains, or while the displayed copy still needs to catch up.
  const holdPending = workPending || label !== targetPhase;
  const visible = useMinVisible(holdPending, minVisibleMs, label);

  useEffect(() => {
    if (snapEpoch == null || snapEpoch === snapEpochRef.current) return;
    snapEpochRef.current = snapEpoch;
    labelShownAtRef.current = performance.now();
    setLabel(targetPhase);
  }, [snapEpoch, targetPhase]);

  useEffect(() => {
    if (!visible) {
      if (!workPending) {
        labelShownAtRef.current = null;
        if (label !== targetPhase) setLabel(targetPhase);
      }
      return;
    }

    if (labelShownAtRef.current == null) {
      labelShownAtRef.current = performance.now();
    }

    if (label === targetPhase) return;

    const remaining = Math.max(0, minVisibleMs - (performance.now() - labelShownAtRef.current));

    const advance = () => {
      labelShownAtRef.current = performance.now();
      setLabel(targetPhase);
    };

    // Already met the minimum — swap immediately (no setTimeout(0) gap).
    if (remaining === 0) {
      advance();
      return;
    }

    const timer = window.setTimeout(advance, remaining);
    return () => window.clearTimeout(timer);
  }, [targetPhase, label, visible, workPending, minVisibleMs]);

  return { visible, label };
}
